// src/schemas/property-record.schema.ts
import { z } from "zod";
import { DealInputSchema } from "@roi/core";
var PropertyStatusSchema = z.enum(["active", "archived", "draft"]);
var PropertyRecordSchema = z.object({
  /** Unique identifier */
  id: z.string().uuid(),
  /** User-defined name for the property */
  name: z.string().min(1).max(200),
  /** Property address (optional) */
  address: z.string().max(500).optional(),
  /** City */
  city: z.string().max(100).optional(),
  /** State/Province */
  state: z.string().max(50).optional(),
  /** ZIP/Postal code */
  zipCode: z.string().max(20).optional(),
  /** Property type */
  propertyType: z.enum([
    "single_family",
    "multi_family",
    "condo",
    "townhouse",
    "duplex",
    "triplex",
    "fourplex",
    "apartment",
    "commercial",
    "mixed_use",
    "other"
  ]).optional(),
  /** Number of units (for multi-family) */
  units: z.number().int().positive().optional(),
  /** User-defined tags for organization */
  tags: z.array(z.string().max(50)).max(20).default([]),
  /** Notes/comments */
  notes: z.string().max(5e3).optional(),
  /** The deal input data */
  dealInput: DealInputSchema,
  /** Record status */
  status: PropertyStatusSchema.default("active"),
  /** Source URL (e.g., Zillow, Redfin link) */
  sourceUrl: z.string().url().optional(),
  /** Thumbnail image URL */
  imageUrl: z.string().url().optional(),
  /** Whether this is marked as favorite */
  isFavorite: z.boolean().default(false),
  /** Creation timestamp */
  createdAt: z.date(),
  /** Last update timestamp */
  updatedAt: z.date(),
  /** Schema version for migrations */
  schemaVersion: z.number().int().positive().default(1)
});
var CreatePropertyRecordSchema = PropertyRecordSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  schemaVersion: true
});
var UpdatePropertyRecordSchema = PropertyRecordSchema.partial().required({
  id: true
});
var PropertySummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  propertyType: PropertyRecordSchema.shape.propertyType,
  tags: z.array(z.string()),
  status: PropertyStatusSchema,
  isFavorite: z.boolean(),
  purchasePrice: z.number(),
  monthlyRent: z.number(),
  capRate: z.number().optional(),
  cashOnCash: z.number().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});

// src/adapters/dexie/dexie-repository.ts
import { v4 as uuidv4 } from "uuid";
import { analyze } from "@roi/core";

// src/adapters/dexie/database.ts
import Dexie from "dexie";
var RoiDatabase = class extends Dexie {
  properties;
  constructor() {
    super("roi-calculator");
    this.version(1).stores({
      properties: [
        "id",
        "name",
        "status",
        "isFavorite",
        "createdAt",
        "updatedAt",
        "*tags",
        "city",
        "state",
        "propertyType"
      ].join(", ")
    });
  }
};
var dbInstance = null;
function getDatabase() {
  if (!dbInstance) {
    dbInstance = new RoiDatabase();
  }
  return dbInstance;
}
async function closeDatabase() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
async function deleteDatabase() {
  await closeDatabase();
  await Dexie.delete("roi-calculator");
}

// src/adapters/dexie/dexie-repository.ts
function toSummary(record) {
  let capRate;
  let cashOnCash;
  try {
    const result = analyze(record.dealInput);
    capRate = result.summaryMetrics.capRate;
    cashOnCash = result.summaryMetrics.cashOnCash;
  } catch {
  }
  return {
    id: record.id,
    name: record.name,
    address: record.address,
    city: record.city,
    state: record.state,
    propertyType: record.propertyType,
    tags: record.tags,
    status: record.status,
    isFavorite: record.isFavorite,
    purchasePrice: record.dealInput.purchase.purchasePrice,
    monthlyRent: record.dealInput.income.monthlyRent,
    capRate,
    cashOnCash,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt
  };
}
var DexiePropertyRepository = class {
  db;
  constructor(db) {
    this.db = db ?? getDatabase();
  }
  async create(data) {
    const now = /* @__PURE__ */ new Date();
    const record = {
      ...data,
      id: uuidv4(),
      tags: data.tags ?? [],
      status: data.status ?? "active",
      isFavorite: data.isFavorite ?? false,
      createdAt: now,
      updatedAt: now,
      schemaVersion: 1
    };
    const validated = PropertyRecordSchema.parse(record);
    await this.db.properties.add(validated);
    return validated;
  }
  async getById(id) {
    const record = await this.db.properties.get(id);
    return record ?? null;
  }
  async update(data) {
    const existing = await this.getById(data.id);
    if (!existing) {
      throw new Error(`Property not found: ${data.id}`);
    }
    const updated = {
      ...existing,
      ...data,
      updatedAt: /* @__PURE__ */ new Date()
    };
    const validated = PropertyRecordSchema.parse(updated);
    await this.db.properties.put(validated);
    return validated;
  }
  async delete(id) {
    await this.db.properties.delete(id);
  }
  async list(options) {
    const { filters, sort, pagination } = options ?? {};
    let collection = this.db.properties.toCollection();
    if (filters) {
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        collection = collection.filter((p) => statuses.includes(p.status));
      }
      if (filters.tags && filters.tags.length > 0) {
        collection = collection.filter(
          (p) => filters.tags.some((tag) => p.tags.includes(tag))
        );
      }
      if (filters.propertyType) {
        const types = Array.isArray(filters.propertyType) ? filters.propertyType : [filters.propertyType];
        collection = collection.filter((p) => p.propertyType != null && types.includes(p.propertyType));
      }
      if (filters.isFavorite !== void 0) {
        collection = collection.filter((p) => p.isFavorite === filters.isFavorite);
      }
      if (filters.city) {
        const cityLower = filters.city.toLowerCase();
        collection = collection.filter(
          (p) => p.city?.toLowerCase().includes(cityLower) === true
        );
      }
      if (filters.state) {
        const stateLower = filters.state.toLowerCase();
        collection = collection.filter(
          (p) => p.state?.toLowerCase() === stateLower
        );
      }
      if (filters.minPrice !== void 0) {
        collection = collection.filter(
          (p) => p.dealInput.purchase.purchasePrice >= filters.minPrice
        );
      }
      if (filters.maxPrice !== void 0) {
        collection = collection.filter(
          (p) => p.dealInput.purchase.purchasePrice <= filters.maxPrice
        );
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        collection = collection.filter((p) => {
          const searchableText = [p.name, p.address, p.notes, p.city, p.state].filter(Boolean).join(" ").toLowerCase();
          return searchableText.includes(searchLower);
        });
      }
    }
    const allRecords = await collection.toArray();
    const total = allRecords.length;
    let sortedRecords = [...allRecords];
    if (sort) {
      sortedRecords.sort((a, b) => {
        let aVal = 0;
        let bVal = 0;
        switch (sort.field) {
          case "name":
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case "createdAt":
            aVal = a.createdAt.getTime();
            bVal = b.createdAt.getTime();
            break;
          case "updatedAt":
            aVal = a.updatedAt.getTime();
            bVal = b.updatedAt.getTime();
            break;
          case "purchasePrice":
            aVal = a.dealInput.purchase.purchasePrice;
            bVal = b.dealInput.purchase.purchasePrice;
            break;
          case "capRate":
          case "cashOnCash":
            try {
              const aResult = analyze(a.dealInput);
              const bResult = analyze(b.dealInput);
              aVal = sort.field === "capRate" ? aResult.summaryMetrics.capRate : aResult.summaryMetrics.cashOnCash;
              bVal = sort.field === "capRate" ? bResult.summaryMetrics.capRate : bResult.summaryMetrics.cashOnCash;
            } catch {
              aVal = 0;
              bVal = 0;
            }
            break;
        }
        if (aVal < bVal) return sort.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sort.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      sortedRecords.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    }
    const offset = pagination?.offset ?? 0;
    const limit = pagination?.limit ?? 50;
    const paginatedRecords = sortedRecords.slice(offset, offset + limit);
    const summaries = paginatedRecords.map(toSummary);
    return {
      items: summaries,
      total,
      offset,
      limit,
      hasMore: offset + limit < total
    };
  }
  async getAllTags() {
    const records = await this.db.properties.toArray();
    const tagSet = /* @__PURE__ */ new Set();
    for (const record of records) {
      for (const tag of record.tags) {
        tagSet.add(tag);
      }
    }
    return Array.from(tagSet).sort();
  }
  async getAllCities() {
    const records = await this.db.properties.toArray();
    const citySet = /* @__PURE__ */ new Set();
    for (const record of records) {
      if (record.city) {
        citySet.add(record.city);
      }
    }
    return Array.from(citySet).sort();
  }
  async bulkDelete(ids) {
    await this.db.properties.bulkDelete(ids);
  }
  async archive(id) {
    return this.update({ id, status: "archived" });
  }
  async restore(id) {
    return this.update({ id, status: "active" });
  }
  async toggleFavorite(id) {
    const existing = await this.getById(id);
    if (!existing) {
      throw new Error(`Property not found: ${id}`);
    }
    return this.update({ id, isFavorite: !existing.isFavorite });
  }
  async getCountByStatus() {
    const records = await this.db.properties.toArray();
    const counts = {
      active: 0,
      archived: 0,
      draft: 0
    };
    for (const record of records) {
      counts[record.status]++;
    }
    return counts;
  }
  async exportAll() {
    return this.db.properties.toArray();
  }
  async importAll(records, options) {
    const result = { imported: 0, skipped: 0, errors: [] };
    for (const record of records) {
      try {
        const validated = PropertyRecordSchema.parse(record);
        const existing = await this.getById(validated.id);
        if (existing && !options?.overwrite) {
          result.skipped++;
          continue;
        }
        if (existing) {
          await this.db.properties.put(validated);
        } else {
          await this.db.properties.add(validated);
        }
        result.imported++;
      } catch (error) {
        result.errors.push(
          `Failed to import property ${record.id}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    }
    return result;
  }
  async clearAll() {
    await this.db.properties.clear();
  }
};

// src/adapters/supabase/supabase-repository.ts
import { v4 as uuidv42 } from "uuid";
import { analyze as analyze2 } from "@roi/core";
function rowToRecord(row) {
  return {
    id: row.id,
    name: row.name,
    address: row.address ?? void 0,
    city: row.city ?? void 0,
    state: row.state ?? void 0,
    zipCode: row.zip_code ?? void 0,
    propertyType: row.property_type,
    units: row.units ?? void 0,
    tags: row.tags ?? [],
    notes: row.notes ?? void 0,
    dealInput: row.deal_input,
    status: row.status,
    sourceUrl: row.source_url ?? void 0,
    imageUrl: row.image_url ?? void 0,
    isFavorite: row.is_favorite,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    schemaVersion: row.schema_version
  };
}
var VALID_PROPERTY_TYPES = [
  "single_family",
  "multi_family",
  "condo",
  "townhouse",
  "duplex",
  "triplex",
  "fourplex",
  "apartment",
  "commercial",
  "mixed_use",
  "other"
];
function normalizePropertyType(propertyType) {
  if (!propertyType) return null;
  const normalized = propertyType.toLowerCase().replace(/\s+/g, "_");
  if (VALID_PROPERTY_TYPES.includes(normalized)) {
    return normalized;
  }
  const mappings = {
    "sfr": "single_family",
    "single": "single_family",
    "singlefamily": "single_family",
    "multi": "multi_family",
    "multifamily": "multi_family",
    "mfr": "multi_family",
    "apt": "apartment",
    "apartments": "apartment",
    "town_home": "townhouse",
    "townhome": "townhouse",
    "town_house": "townhouse"
  };
  if (mappings[normalized]) {
    return mappings[normalized];
  }
  return "other";
}
function recordToInsert(record, userId) {
  return {
    id: record.id,
    user_id: userId,
    name: record.name,
    address: record.address ?? null,
    city: record.city ?? null,
    state: record.state ?? null,
    zip_code: record.zipCode ?? null,
    property_type: normalizePropertyType(record.propertyType),
    units: record.units ?? null,
    tags: record.tags,
    notes: record.notes ?? null,
    deal_input: record.dealInput,
    status: record.status,
    source_url: record.sourceUrl ?? null,
    image_url: record.imageUrl ?? null,
    is_favorite: record.isFavorite,
    local_id: record.id,
    // Use the same ID for local tracking
    schema_version: record.schemaVersion,
    created_at: record.createdAt.toISOString(),
    updated_at: record.updatedAt.toISOString()
  };
}
function recordToUpdate(data) {
  const update = {};
  if (data.name !== void 0) update.name = data.name;
  if (data.address !== void 0) update.address = data.address ?? null;
  if (data.city !== void 0) update.city = data.city ?? null;
  if (data.state !== void 0) update.state = data.state ?? null;
  if (data.zipCode !== void 0) update.zip_code = data.zipCode ?? null;
  if (data.propertyType !== void 0) update.property_type = normalizePropertyType(data.propertyType);
  if (data.units !== void 0) update.units = data.units ?? null;
  if (data.tags !== void 0) update.tags = data.tags;
  if (data.notes !== void 0) update.notes = data.notes ?? null;
  if (data.dealInput !== void 0) update.deal_input = data.dealInput;
  if (data.status !== void 0) update.status = data.status;
  if (data.sourceUrl !== void 0) update.source_url = data.sourceUrl ?? null;
  if (data.imageUrl !== void 0) update.image_url = data.imageUrl ?? null;
  if (data.isFavorite !== void 0) update.is_favorite = data.isFavorite;
  if (data.schemaVersion !== void 0) update.schema_version = data.schemaVersion;
  update.updated_at = (/* @__PURE__ */ new Date()).toISOString();
  return update;
}
function toSummary2(record) {
  let capRate;
  let cashOnCash;
  try {
    const result = analyze2(record.dealInput);
    capRate = result.summaryMetrics.capRate;
    cashOnCash = result.summaryMetrics.cashOnCash;
  } catch {
  }
  return {
    id: record.id,
    name: record.name,
    address: record.address,
    city: record.city,
    state: record.state,
    propertyType: record.propertyType,
    tags: record.tags,
    status: record.status,
    isFavorite: record.isFavorite,
    purchasePrice: record.dealInput.purchase.purchasePrice,
    monthlyRent: record.dealInput.income.monthlyRent,
    capRate,
    cashOnCash,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt
  };
}
var SupabasePropertyRepository = class {
  client;
  userId;
  constructor(client, userId) {
    this.client = client;
    this.userId = userId;
  }
  async create(data) {
    const now = /* @__PURE__ */ new Date();
    const record = {
      ...data,
      id: uuidv42(),
      tags: data.tags ?? [],
      status: data.status ?? "active",
      isFavorite: data.isFavorite ?? false,
      createdAt: now,
      updatedAt: now,
      schemaVersion: 1
    };
    const validated = PropertyRecordSchema.parse(record);
    const insertData = recordToInsert(validated, this.userId);
    const { error } = await this.client.from("properties").insert(insertData);
    if (error) {
      throw new Error(`Failed to create property: ${error.message}`);
    }
    return validated;
  }
  async getById(id) {
    const { data, error } = await this.client.from("properties").select("*").eq("id", id).single();
    if (error) {
      if (error.code === "PGRST116") {
        return null;
      }
      throw new Error(`Failed to get property: ${error.message}`);
    }
    return data ? rowToRecord(data) : null;
  }
  async update(data) {
    const existing = await this.getById(data.id);
    if (!existing) {
      throw new Error(`Property not found: ${data.id}`);
    }
    const updateData = recordToUpdate(data);
    const { data: updated, error } = await this.client.from("properties").update(updateData).eq("id", data.id).select("*").single();
    if (error) {
      throw new Error(`Failed to update property: ${error.message}`);
    }
    const record = rowToRecord(updated);
    return PropertyRecordSchema.parse(record);
  }
  async delete(id) {
    const { error } = await this.client.from("properties").delete().eq("id", id);
    if (error) {
      throw new Error(`Failed to delete property: ${error.message}`);
    }
  }
  async list(options) {
    const { filters, sort, pagination } = options ?? {};
    const offset = pagination?.offset ?? 0;
    const limit = pagination?.limit ?? 50;
    let query = this.client.from("properties").select("*", { count: "exact" });
    if (filters) {
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        query = query.in("status", statuses);
      }
      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps("tags", filters.tags);
      }
      if (filters.propertyType) {
        const types = Array.isArray(filters.propertyType) ? filters.propertyType : [filters.propertyType];
        query = query.in("property_type", types);
      }
      if (filters.isFavorite !== void 0) {
        query = query.eq("is_favorite", filters.isFavorite);
      }
      if (filters.city) {
        query = query.ilike("city", `%${filters.city}%`);
      }
      if (filters.state) {
        query = query.ilike("state", filters.state);
      }
      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,address.ilike.%${filters.search}%,notes.ilike.%${filters.search}%,city.ilike.%${filters.search}%,state.ilike.%${filters.search}%`
        );
      }
      if (filters.minPrice !== void 0) {
        query = query.gte("deal_input->purchase->purchasePrice", filters.minPrice);
      }
      if (filters.maxPrice !== void 0) {
        query = query.lte("deal_input->purchase->purchasePrice", filters.maxPrice);
      }
    }
    if (sort) {
      const columnMap = {
        name: "name",
        createdAt: "created_at",
        updatedAt: "updated_at",
        purchasePrice: "deal_input->purchase->purchasePrice"
      };
      const column = columnMap[sort.field];
      if (column) {
        query = query.order(column, { ascending: sort.direction === "asc" });
      }
    } else {
      query = query.order("updated_at", { ascending: false });
    }
    query = query.range(offset, offset + limit - 1);
    const { data, error, count } = await query;
    if (error) {
      throw new Error(`Failed to list properties: ${error.message}`);
    }
    const records = (data ?? []).map((row) => rowToRecord(row));
    let sortedRecords = records;
    if (sort && (sort.field === "capRate" || sort.field === "cashOnCash")) {
      sortedRecords = [...records].sort((a, b) => {
        let aVal = 0;
        let bVal = 0;
        try {
          const aResult = analyze2(a.dealInput);
          const bResult = analyze2(b.dealInput);
          aVal = sort.field === "capRate" ? aResult.summaryMetrics.capRate : aResult.summaryMetrics.cashOnCash;
          bVal = sort.field === "capRate" ? bResult.summaryMetrics.capRate : bResult.summaryMetrics.cashOnCash;
        } catch {
        }
        if (aVal < bVal) return sort.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    const summaries = sortedRecords.map(toSummary2);
    const total = count ?? 0;
    return {
      items: summaries,
      total,
      offset,
      limit,
      hasMore: offset + limit < total
    };
  }
  async getAllTags() {
    const { data, error } = await this.client.from("properties").select("tags");
    if (error) {
      throw new Error(`Failed to get tags: ${error.message}`);
    }
    const tagSet = /* @__PURE__ */ new Set();
    for (const row of data ?? []) {
      for (const tag of row.tags ?? []) {
        tagSet.add(tag);
      }
    }
    return Array.from(tagSet).sort();
  }
  async getAllCities() {
    const { data, error } = await this.client.from("properties").select("city").not("city", "is", null);
    if (error) {
      throw new Error(`Failed to get cities: ${error.message}`);
    }
    const citySet = /* @__PURE__ */ new Set();
    for (const row of data ?? []) {
      if (row.city) {
        citySet.add(row.city);
      }
    }
    return Array.from(citySet).sort();
  }
  async bulkDelete(ids) {
    const { error } = await this.client.from("properties").delete().in("id", ids);
    if (error) {
      throw new Error(`Failed to bulk delete properties: ${error.message}`);
    }
  }
  async archive(id) {
    return this.update({ id, status: "archived" });
  }
  async restore(id) {
    return this.update({ id, status: "active" });
  }
  async toggleFavorite(id) {
    const existing = await this.getById(id);
    if (!existing) {
      throw new Error(`Property not found: ${id}`);
    }
    return this.update({ id, isFavorite: !existing.isFavorite });
  }
  async getCountByStatus() {
    const counts = {
      active: 0,
      archived: 0,
      draft: 0
    };
    const { data, error } = await this.client.from("properties").select("status");
    if (error) {
      throw new Error(`Failed to get counts: ${error.message}`);
    }
    for (const row of data ?? []) {
      const status = row.status;
      if (status in counts) {
        counts[status]++;
      }
    }
    return counts;
  }
  async exportAll() {
    const { data, error } = await this.client.from("properties").select("*");
    if (error) {
      throw new Error(`Failed to export properties: ${error.message}`);
    }
    return (data ?? []).map((row) => rowToRecord(row));
  }
  async importAll(records, options) {
    const result = { imported: 0, skipped: 0, errors: [] };
    for (const record of records) {
      try {
        const validated = PropertyRecordSchema.parse(record);
        const existing = await this.getById(validated.id);
        if (existing && !options?.overwrite) {
          result.skipped++;
          continue;
        }
        const insertData = recordToInsert(validated, this.userId);
        if (existing) {
          const { error } = await this.client.from("properties").update(insertData).eq("id", validated.id);
          if (error) throw error;
        } else {
          const { error } = await this.client.from("properties").insert(insertData);
          if (error) throw error;
        }
        result.imported++;
      } catch (error) {
        result.errors.push(
          `Failed to import property ${record.id}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    }
    return result;
  }
  async clearAll() {
    const { error } = await this.client.from("properties").delete().neq("id", "");
    if (error) {
      throw new Error(`Failed to clear properties: ${error.message}`);
    }
  }
};

// src/adapters/hybrid/hybrid-repository.ts
var HybridPropertyRepository = class {
  localRepo;
  cloudRepo;
  pendingOps = /* @__PURE__ */ new Map();
  isSyncing = false;
  syncInterval = null;
  onSyncStatusChange;
  constructor(localRepo, cloudRepo = null, options) {
    this.localRepo = localRepo;
    this.cloudRepo = cloudRepo;
    this.onSyncStatusChange = options?.onSyncStatusChange;
    if (options?.autoSync && cloudRepo) {
      this.startAutoSync(options.syncIntervalMs ?? 3e4);
    }
  }
  /**
   * Set or update the cloud repository
   */
  setCloudRepository(cloudRepo) {
    this.cloudRepo = cloudRepo;
    if (cloudRepo && this.pendingOps.size > 0) {
      this.syncPendingOperations();
    }
  }
  /**
   * Check if cloud sync is available
   */
  isCloudAvailable() {
    return this.cloudRepo !== null;
  }
  /**
   * Get number of pending sync operations
   */
  getPendingCount() {
    return this.pendingOps.size;
  }
  /**
   * Check if currently syncing
   */
  isSyncingNow() {
    return this.isSyncing;
  }
  /**
   * Start auto-sync interval
   */
  startAutoSync(intervalMs = 3e4) {
    this.stopAutoSync();
    this.syncInterval = setInterval(() => {
      if (this.cloudRepo && !this.isSyncing && this.pendingOps.size > 0) {
        this.syncPendingOperations();
      }
    }, intervalMs);
  }
  /**
   * Stop auto-sync interval
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
  /**
   * Manually trigger sync of pending operations
   */
  async syncPendingOperations() {
    if (!this.cloudRepo || this.isSyncing) {
      return { success: 0, failed: 0 };
    }
    this.isSyncing = true;
    this.onSyncStatusChange?.(true, this.pendingOps.size);
    let success = 0;
    let failed = 0;
    const ops = Array.from(this.pendingOps.values()).sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    for (const op of ops) {
      try {
        switch (op.type) {
          case "create":
            if (op.data) {
              await this.cloudRepo.importAll([op.data], { overwrite: false });
            }
            break;
          case "update":
            if (op.data) {
              await this.cloudRepo.importAll([op.data], { overwrite: true });
            }
            break;
          case "delete":
            await this.cloudRepo.delete(op.id);
            break;
        }
        this.pendingOps.delete(op.id);
        success++;
      } catch (error) {
        console.error(`Failed to sync operation ${op.type} for ${op.id}:`, error);
        failed++;
      }
    }
    this.isSyncing = false;
    this.onSyncStatusChange?.(false, this.pendingOps.size);
    return { success, failed };
  }
  /**
   * Add an operation to the pending queue
   */
  queueOperation(op) {
    this.pendingOps.set(op.id, op);
    this.onSyncStatusChange?.(this.isSyncing, this.pendingOps.size);
    if (this.cloudRepo && !this.isSyncing) {
      setTimeout(() => this.syncPendingOperations(), 1e3);
    }
  }
  // PropertyRepository implementation - Local-first approach
  async create(data) {
    const record = await this.localRepo.create(data);
    this.queueOperation({
      id: record.id,
      type: "create",
      timestamp: /* @__PURE__ */ new Date(),
      data: record
    });
    return record;
  }
  async getById(id) {
    return this.localRepo.getById(id);
  }
  async update(data) {
    const record = await this.localRepo.update(data);
    this.queueOperation({
      id: record.id,
      type: "update",
      timestamp: /* @__PURE__ */ new Date(),
      data: record
    });
    return record;
  }
  async delete(id) {
    await this.localRepo.delete(id);
    this.queueOperation({
      id,
      type: "delete",
      timestamp: /* @__PURE__ */ new Date()
    });
  }
  async list(options) {
    return this.localRepo.list(options);
  }
  async getAllTags() {
    return this.localRepo.getAllTags();
  }
  async getAllCities() {
    return this.localRepo.getAllCities();
  }
  async bulkDelete(ids) {
    await this.localRepo.bulkDelete(ids);
    for (const id of ids) {
      this.queueOperation({
        id,
        type: "delete",
        timestamp: /* @__PURE__ */ new Date()
      });
    }
  }
  async archive(id) {
    const record = await this.localRepo.archive(id);
    this.queueOperation({
      id: record.id,
      type: "update",
      timestamp: /* @__PURE__ */ new Date(),
      data: record
    });
    return record;
  }
  async restore(id) {
    const record = await this.localRepo.restore(id);
    this.queueOperation({
      id: record.id,
      type: "update",
      timestamp: /* @__PURE__ */ new Date(),
      data: record
    });
    return record;
  }
  async toggleFavorite(id) {
    const record = await this.localRepo.toggleFavorite(id);
    this.queueOperation({
      id: record.id,
      type: "update",
      timestamp: /* @__PURE__ */ new Date(),
      data: record
    });
    return record;
  }
  async getCountByStatus() {
    return this.localRepo.getCountByStatus();
  }
  async exportAll() {
    return this.localRepo.exportAll();
  }
  async importAll(records, options) {
    const result = await this.localRepo.importAll(records, options);
    for (const record of records) {
      this.queueOperation({
        id: record.id,
        type: "create",
        timestamp: /* @__PURE__ */ new Date(),
        data: record
      });
    }
    return result;
  }
  async clearAll() {
    const records = await this.localRepo.exportAll();
    await this.localRepo.clearAll();
    for (const record of records) {
      this.queueOperation({
        id: record.id,
        type: "delete",
        timestamp: /* @__PURE__ */ new Date()
      });
    }
  }
  /**
   * Pull all data from cloud to local (for initial sync or recovery)
   */
  async pullFromCloud() {
    if (!this.cloudRepo) {
      return { imported: 0, errors: ["Cloud repository not configured"] };
    }
    const cloudRecords = await this.cloudRepo.exportAll();
    return this.localRepo.importAll(cloudRecords, { overwrite: true });
  }
  /**
   * Push all local data to cloud (for initial sync or recovery)
   */
  async pushToCloud() {
    if (!this.cloudRepo) {
      return { uploaded: 0, errors: ["Cloud repository not configured"] };
    }
    const localRecords = await this.localRepo.exportAll();
    const result = await this.cloudRepo.importAll(localRecords, { overwrite: true });
    return { uploaded: result.imported, errors: result.errors };
  }
  /**
   * Cleanup resources
   */
  dispose() {
    this.stopAutoSync();
  }
};

// src/services/export-import.service.ts
var ExportImportServiceImpl = class {
  /**
   * Export properties to JSON string
   */
  exportToJson(properties) {
    const exportData = {
      version: 1,
      exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
      count: properties.length,
      properties: properties.map((p) => ({
        ...p,
        createdAt: p.createdAt.toISOString(),
        updatedAt: p.updatedAt.toISOString()
      }))
    };
    return JSON.stringify(exportData, null, 2);
  }
  /**
   * Parse JSON string to property records
   */
  parseJson(json) {
    const parsed = JSON.parse(json);
    const rawProperties = parsed.properties ?? parsed;
    if (!Array.isArray(rawProperties)) {
      throw new Error("Invalid JSON format: expected an array of properties");
    }
    return rawProperties.map((raw, index) => {
      try {
        const record = raw;
        if (typeof record.createdAt === "string") {
          record.createdAt = new Date(record.createdAt);
        }
        if (typeof record.updatedAt === "string") {
          record.updatedAt = new Date(record.updatedAt);
        }
        return PropertyRecordSchema.parse(record);
      } catch (error) {
        throw new Error(
          `Invalid property at index ${index}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    });
  }
  /**
   * Export properties to CSV string
   */
  exportToCsv(properties) {
    const columns = [
      "id",
      "name",
      "address",
      "city",
      "state",
      "zipCode",
      "propertyType",
      "status",
      "isFavorite",
      "tags",
      "notes",
      "sourceUrl",
      "purchasePrice",
      "closingCosts",
      "rehabCosts",
      "afterRepairValue",
      "downPaymentPercent",
      "interestRate",
      "loanTermYears",
      "loanType",
      "monthlyRent",
      "otherMonthlyIncome",
      "vacancyRate",
      "annualRentGrowth",
      "propertyTax",
      "insurance",
      "hoaMonthly",
      "maintenancePercent",
      "propertyManagementPercent",
      "annualAppreciation",
      "holdingPeriodYears",
      "sellingCostsPercent",
      "createdAt",
      "updatedAt"
    ];
    const header = columns.join(",");
    const rows = properties.map((p) => {
      const values = [
        p.id,
        this.escapeCsvValue(p.name),
        this.escapeCsvValue(p.address ?? ""),
        this.escapeCsvValue(p.city ?? ""),
        this.escapeCsvValue(p.state ?? ""),
        p.zipCode ?? "",
        p.propertyType ?? "",
        p.status,
        p.isFavorite ? "true" : "false",
        this.escapeCsvValue(p.tags.join(";")),
        this.escapeCsvValue(p.notes ?? ""),
        p.sourceUrl ?? "",
        p.dealInput.purchase.purchasePrice,
        p.dealInput.purchase.closingCosts,
        p.dealInput.purchase.rehabCosts,
        p.dealInput.purchase.afterRepairValue ?? "",
        p.dealInput.financing.downPaymentPercent,
        p.dealInput.financing.interestRate,
        p.dealInput.financing.loanTermYears,
        p.dealInput.financing.loanType,
        p.dealInput.income.monthlyRent,
        p.dealInput.income.otherMonthlyIncome,
        p.dealInput.income.vacancyRate,
        p.dealInput.income.annualRentGrowth,
        p.dealInput.expenses.propertyTax,
        p.dealInput.expenses.insurance,
        p.dealInput.expenses.hoaMonthly,
        p.dealInput.expenses.maintenancePercent,
        p.dealInput.expenses.propertyManagementPercent,
        p.dealInput.appreciationExit.annualAppreciation,
        p.dealInput.appreciationExit.holdingPeriodYears,
        p.dealInput.appreciationExit.sellingCostsPercent,
        p.createdAt.toISOString(),
        p.updatedAt.toISOString()
      ];
      return values.join(",");
    });
    return [header, ...rows].join("\n");
  }
  /**
   * Parse CSV string to property records (best effort)
   */
  parseCsv(csv) {
    const lines = csv.split("\n").filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error("CSV must have at least a header row and one data row");
    }
    const headers = this.parseCsvLine(lines[0]);
    const records = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCsvLine(lines[i]);
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] ?? "";
      });
      try {
        const record = {
          name: row.name || `Imported Property ${i}`,
          address: row.address || void 0,
          city: row.city || void 0,
          state: row.state || void 0,
          zipCode: row.zipCode || void 0,
          propertyType: this.parsePropertyType(row.propertyType),
          status: this.parseStatus(row.status),
          isFavorite: row.isFavorite === "true",
          tags: row.tags ? row.tags.split(";").filter(Boolean) : [],
          notes: row.notes || void 0,
          sourceUrl: row.sourceUrl || void 0,
          dealInput: {
            schemaVersion: 1,
            propertyInfo: {
              name: row.name || `Imported Property ${i}`,
              address: row.address || "",
              city: row.city || "",
              state: row.state || "",
              zipCode: row.zipCode || ""
            },
            purchase: {
              purchasePrice: this.parseNumber(row.purchasePrice, 0),
              closingCosts: this.parseNumber(row.closingCosts, 0),
              rehabCosts: this.parseNumber(row.rehabCosts, 0),
              furnishingCosts: 0,
              afterRepairValue: row.afterRepairValue ? this.parseNumber(row.afterRepairValue, void 0) : void 0
            },
            financing: {
              downPaymentPercent: this.parseNumber(row.downPaymentPercent, 20),
              interestRate: this.parseNumber(row.interestRate, 7),
              loanTermYears: this.parseNumber(row.loanTermYears, 30),
              loanType: row.loanType || "conventional",
              pointsPaid: 0,
              lenderFees: 0
            },
            income: {
              monthlyRent: this.parseNumber(row.monthlyRent, 0),
              otherMonthlyIncome: this.parseNumber(row.otherMonthlyIncome, 0),
              vacancyRate: this.parseNumber(row.vacancyRate, 5),
              annualRentGrowth: this.parseNumber(row.annualRentGrowth, 3)
            },
            expenses: {
              propertyTax: this.parseNumber(row.propertyTax, 0),
              insurance: this.parseNumber(row.insurance, 0),
              hoaMonthly: this.parseNumber(row.hoaMonthly, 0),
              maintenancePercent: this.parseNumber(row.maintenancePercent, 5),
              capexPercent: 5,
              propertyManagementPercent: this.parseNumber(row.propertyManagementPercent, 8),
              utilitiesMonthly: 0,
              leasingCosts: 0,
              otherExpenses: 0,
              annualExpenseGrowth: 2
            },
            appreciationExit: {
              annualAppreciation: this.parseNumber(row.annualAppreciation, 3),
              holdingPeriodYears: this.parseNumber(row.holdingPeriodYears, 5),
              sellingCostsPercent: this.parseNumber(row.sellingCostsPercent, 6)
            }
          }
        };
        records.push(record);
      } catch {
        console.warn(`Skipping invalid row ${i + 1}`);
      }
    }
    return records;
  }
  /**
   * Download data as a file (browser only)
   */
  downloadFile(content, filename, mimeType) {
    if (typeof window === "undefined" || typeof document === "undefined") {
      throw new Error("downloadFile is only available in browser environment");
    }
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  // Helper methods
  escapeCsvValue(value) {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }
  parseCsvLine(line) {
    const values = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (inQuotes) {
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ",") {
          values.push(current);
          current = "";
        } else {
          current += char;
        }
      }
    }
    values.push(current);
    return values;
  }
  parseNumber(value, defaultValue) {
    if (!value || value.trim() === "") {
      return defaultValue;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  parsePropertyType(value) {
    const validTypes = [
      "single_family",
      "multi_family",
      "condo",
      "townhouse",
      "duplex",
      "triplex",
      "fourplex",
      "apartment",
      "commercial",
      "mixed_use",
      "other"
    ];
    if (value && validTypes.includes(value)) {
      return value;
    }
    return void 0;
  }
  parseStatus(value) {
    if (value === "archived" || value === "draft") {
      return value;
    }
    return "active";
  }
};
var serviceInstance = null;
function getExportImportService() {
  if (!serviceInstance) {
    serviceInstance = new ExportImportServiceImpl();
  }
  return serviceInstance;
}

// src/services/sync.service.ts
var SyncService = class {
  localRepo;
  cloudRepo;
  lastSyncTime = null;
  constructor(localRepo, cloudRepo = null) {
    this.localRepo = localRepo;
    this.cloudRepo = cloudRepo;
  }
  /**
   * Set or update the cloud repository (e.g., when user logs in)
   */
  setCloudRepository(cloudRepo) {
    this.cloudRepo = cloudRepo;
  }
  /**
   * Check if sync is available (cloud repo configured)
   */
  isSyncAvailable() {
    return this.cloudRepo !== null;
  }
  /**
   * Get the last sync time
   */
  getLastSyncTime() {
    return this.lastSyncTime;
  }
  /**
   * Perform a full sync between local and cloud
   */
  async sync(options = {}) {
    const startedAt = /* @__PURE__ */ new Date();
    const result = {
      success: false,
      totalProcessed: 0,
      uploaded: 0,
      downloaded: 0,
      conflicts: [],
      errors: [],
      startedAt,
      completedAt: startedAt
    };
    if (!this.cloudRepo) {
      result.errors.push({
        id: "system",
        status: "error",
        direction: "upload",
        error: "Cloud repository not configured"
      });
      result.completedAt = /* @__PURE__ */ new Date();
      return result;
    }
    const { conflictResolution = "local", onProgress } = options;
    try {
      onProgress?.({ phase: "preparing", current: 0, total: 100, message: "Loading local records..." });
      const localRecords = await this.localRepo.exportAll();
      onProgress?.({ phase: "preparing", current: 50, total: 100, message: "Loading cloud records..." });
      const cloudRecords = await this.cloudRepo.exportAll();
      const localMap = /* @__PURE__ */ new Map();
      const cloudMap = /* @__PURE__ */ new Map();
      for (const record of localRecords) {
        localMap.set(record.id, record);
      }
      for (const record of cloudRecords) {
        cloudMap.set(record.id, record);
      }
      const allIds = /* @__PURE__ */ new Set([...localMap.keys(), ...cloudMap.keys()]);
      const totalItems = allIds.size;
      let processed = 0;
      onProgress?.({ phase: "uploading", current: 0, total: totalItems, message: "Uploading new records..." });
      for (const id of allIds) {
        const localRecord = localMap.get(id);
        const cloudRecord = cloudMap.get(id);
        if (localRecord && !cloudRecord) {
          try {
            await this.cloudRepo.importAll([localRecord], { overwrite: true });
            result.uploaded++;
          } catch (error) {
            result.errors.push({
              id,
              status: "error",
              direction: "upload",
              error: error instanceof Error ? error.message : "Unknown error"
            });
          }
        } else if (!localRecord && cloudRecord) {
          try {
            await this.localRepo.importAll([cloudRecord], { overwrite: true });
            result.downloaded++;
          } catch (error) {
            result.errors.push({
              id,
              status: "error",
              direction: "download",
              error: error instanceof Error ? error.message : "Unknown error"
            });
          }
        } else if (localRecord && cloudRecord) {
          if (this.hasConflict(localRecord, cloudRecord)) {
            if (conflictResolution === "manual") {
              result.conflicts.push({ localRecord, cloudRecord });
            } else {
              const winner = conflictResolution === "local" ? localRecord : cloudRecord;
              try {
                if (conflictResolution === "local") {
                  await this.cloudRepo.importAll([winner], { overwrite: true });
                  result.uploaded++;
                } else {
                  await this.localRepo.importAll([winner], { overwrite: true });
                  result.downloaded++;
                }
              } catch (error) {
                result.errors.push({
                  id,
                  status: "error",
                  direction: conflictResolution === "local" ? "upload" : "download",
                  error: error instanceof Error ? error.message : "Unknown error"
                });
              }
            }
          }
        }
        processed++;
        result.totalProcessed = processed;
        onProgress?.({
          phase: processed < totalItems ? "uploading" : "completed",
          current: processed,
          total: totalItems
        });
      }
      result.success = result.errors.length === 0 && result.conflicts.length === 0;
      result.completedAt = /* @__PURE__ */ new Date();
      this.lastSyncTime = result.completedAt;
      onProgress?.({ phase: "completed", current: totalItems, total: totalItems, message: "Sync complete" });
    } catch (error) {
      result.errors.push({
        id: "system",
        status: "error",
        direction: "upload",
        error: error instanceof Error ? error.message : "Unknown error"
      });
      result.completedAt = /* @__PURE__ */ new Date();
    }
    return result;
  }
  /**
   * Resolve a conflict manually
   */
  async resolveConflict(conflict, resolution) {
    if (!this.cloudRepo) {
      return {
        id: conflict.localRecord.id,
        status: "error",
        direction: "upload",
        error: "Cloud repository not configured"
      };
    }
    try {
      if (resolution === "local") {
        await this.cloudRepo.importAll([conflict.localRecord], { overwrite: true });
      } else {
        await this.localRepo.importAll([conflict.cloudRecord], { overwrite: true });
      }
      return {
        id: conflict.localRecord.id,
        status: "updated",
        direction: resolution === "local" ? "upload" : "download"
      };
    } catch (error) {
      return {
        id: conflict.localRecord.id,
        status: "error",
        direction: resolution === "local" ? "upload" : "download",
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }
  /**
   * Migrate all local data to cloud (one-time operation for new users)
   */
  async migrateToCloud(onProgress) {
    const startedAt = /* @__PURE__ */ new Date();
    const result = {
      success: false,
      totalProcessed: 0,
      uploaded: 0,
      downloaded: 0,
      conflicts: [],
      errors: [],
      startedAt,
      completedAt: startedAt
    };
    if (!this.cloudRepo) {
      result.errors.push({
        id: "system",
        status: "error",
        direction: "upload",
        error: "Cloud repository not configured"
      });
      result.completedAt = /* @__PURE__ */ new Date();
      return result;
    }
    try {
      onProgress?.({ phase: "preparing", current: 0, total: 100, message: "Loading local records..." });
      const localRecords = await this.localRepo.exportAll();
      const total = localRecords.length;
      onProgress?.({ phase: "uploading", current: 0, total, message: "Uploading to cloud..." });
      for (let i = 0; i < localRecords.length; i++) {
        const record = localRecords[i];
        try {
          await this.cloudRepo.importAll([record], { overwrite: false });
          result.uploaded++;
        } catch (error) {
          result.errors.push({
            id: record.id,
            status: "error",
            direction: "upload",
            error: error instanceof Error ? error.message : "Unknown error"
          });
        }
        result.totalProcessed++;
        onProgress?.({ phase: "uploading", current: i + 1, total });
      }
      result.success = result.errors.length === 0;
      result.completedAt = /* @__PURE__ */ new Date();
      this.lastSyncTime = result.completedAt;
      onProgress?.({ phase: "completed", current: total, total, message: "Migration complete" });
    } catch (error) {
      result.errors.push({
        id: "system",
        status: "error",
        direction: "upload",
        error: error instanceof Error ? error.message : "Unknown error"
      });
      result.completedAt = /* @__PURE__ */ new Date();
    }
    return result;
  }
  /**
   * Check if two records have conflicting changes
   */
  hasConflict(local, cloud) {
    return local.updatedAt.getTime() !== cloud.updatedAt.getTime() && JSON.stringify(local.dealInput) !== JSON.stringify(cloud.dealInput);
  }
};
var syncServiceInstance = null;
function getSyncService(localRepo, cloudRepo) {
  if (!syncServiceInstance) {
    syncServiceInstance = new SyncService(localRepo, cloudRepo);
  } else if (cloudRepo !== void 0) {
    syncServiceInstance.setCloudRepository(cloudRepo);
  }
  return syncServiceInstance;
}
export {
  CreatePropertyRecordSchema,
  DexiePropertyRepository,
  ExportImportServiceImpl,
  HybridPropertyRepository,
  PropertyRecordSchema,
  PropertyStatusSchema,
  PropertySummarySchema,
  RoiDatabase,
  SupabasePropertyRepository,
  SyncService,
  UpdatePropertyRecordSchema,
  closeDatabase,
  deleteDatabase,
  getDatabase,
  getExportImportService,
  getSyncService
};
//# sourceMappingURL=index.js.map